import { useState, useEffect } from 'react';
import api from '../config/axios';
import { Plus, Monitor as MonitorIcon, Search, Filter, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSocket } from '../contexts/SocketContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import MonitorCard from '../components/Monitors/MonitorCard';
import MonitorModal from '../components/Monitors/MonitorModal';

const Monitors = () => {
    const [monitors, setMonitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingMonitor, setEditingMonitor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, up, down
    const { socket } = useSocket();

    useEffect(() => {
        fetchMonitors();
    }, []);

    useEffect(() => {
        if (!socket) return;

        const handleMonitorUpdate = (data) => {
            setMonitors(prevMonitors =>
                prevMonitors.map(monitor => {
                    if (monitor._id === data.monitorId) {
                        return {
                            ...monitor,
                            status: data.status,
                            uptime: data.uptime,
                            lastChecked: data.timestamp
                        };
                    }
                    return monitor;
                })
            );

            // Check for ownership in the list
            const target = monitors.find(m => m._id === data.monitorId);
            if (target) {
                if (data.status === 'down') {
                    toast.error(`${target.name} is down!`, { icon: '🔴' });
                } else if (data.status === 'up') {
                    toast.success(`${target.name} is back online!`, { icon: '🟢' });
                }
            }
        };

        socket.on('monitorUpdate', handleMonitorUpdate);
        return () => socket.off('monitorUpdate', handleMonitorUpdate);
    }, [socket, monitors]);

    const fetchMonitors = async () => {
        try {
            const response = await api.get('/api/monitors');
            setMonitors(response.data.monitors);
        } catch (error) {
            toast.error('Failed to fetch monitors');
        } finally {
            setLoading(false);
        }
    };

    const handleAddMonitor = async (monitorData) => {
        try {
            const response = await api.post('/api/monitors', monitorData);
            setMonitors([response.data.monitor, ...monitors]);
            setShowAddModal(false);
            toast.success('Monitor deployment successful');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to deploy monitor');
        }
    };

    const handleUpdateMonitor = async (monitorId, updateData) => {
        try {
            const response = await api.put(`/api/monitors/${monitorId}`, updateData);
            setMonitors(monitors.map(m => m._id === monitorId ? response.data.monitor : m));
            setEditingMonitor(null);
            toast.success('Configuration updated');
        } catch (error) {
            toast.error('Update failed');
        }
    };

    const handleDeleteMonitor = async (monitorId) => {
        try {
            await api.delete(`/api/monitors/${monitorId}`);
            setMonitors(monitors.filter(m => m._id !== monitorId));
            toast.success('Monitor decommissioned');
        } catch (error) {
            toast.error('Deletion failed');
        }
    };

    const filteredMonitors = monitors.filter(m => {
        const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.url.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterStatus === 'all' || m.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    return (
        <div className="animate-fade-in pb-12">
            {/* Header section with specialized management UI */}
            <div className="mb-12 space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center space-x-2 text-brand-600 dark:text-brand-400 font-bold text-xs uppercase tracking-[0.2em] mb-3">
                            <div className="h-1 w-8 bg-brand-600 dark:bg-brand-400 rounded-full"></div>
                            <span>Resource Inventory</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-surface-900 dark:text-white tracking-tight">
                            Site Management
                        </h1>
                    </div>

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="btn-primary group !py-4 !px-8 shadow-glow-brand"
                    >
                        <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform" />
                        <span>Deploy New Target</span>
                    </button>
                </div>

                {/* Search & Filter Toolbar */}
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-surface-400 group-focus-within:text-brand-600 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search by node name or endpoint URL..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-2xl py-4 pl-12 pr-4 text-sm focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 outline-none transition-all shadow-premium-sm"
                        />
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="p-1 bg-surface-100 dark:bg-surface-800/80 rounded-2xl border border-surface-200 dark:border-surface-700/50 flex shrink-0 shadow-premium-sm">
                            {[
                                { id: 'all', label: 'All Nodes' },
                                { id: 'up', label: 'Online' },
                                { id: 'down', label: 'Down' }
                            ].map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => setFilterStatus(s.id)}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${filterStatus === s.id
                                        ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-white shadow-premium-sm'
                                        : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-300'
                                        }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        <button className="p-4 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 text-surface-400 hover:text-brand-600 transition-all shadow-premium-sm">
                            <SlidersHorizontal className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid view of monitors */}
            {filteredMonitors.length === 0 ? (
                <div className="card-premium p-20 text-center">
                    <div className="p-5 bg-surface-100 dark:bg-surface-800 rounded-3xl inline-block mb-8">
                        <Search className="h-10 w-10 text-surface-400" />
                    </div>
                    <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-2">No Matching Infrastructure</h3>
                    <p className="text-sm text-surface-500 font-medium">Try adjusting your search query or status filter</p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {filteredMonitors.map((monitor) => (
                        <MonitorCard
                            key={monitor._id}
                            monitor={monitor}
                            onDelete={handleDeleteMonitor}
                            onEdit={setEditingMonitor}
                        />
                    ))}
                </div>
            )}

            {/* Modals */}
            {showAddModal && <MonitorModal onClose={() => setShowAddModal(false)} onSave={handleAddMonitor} />}
            {editingMonitor && (
                <MonitorModal
                    initialData={editingMonitor}
                    onClose={() => setEditingMonitor(null)}
                    onSave={(data) => handleUpdateMonitor(editingMonitor._id, data)}
                />
            )}
        </div>
    );
};

export default Monitors;
